import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { useAuth } from '@hooks';

import { MainMenu, UserMenu } from '@components/molecules';

import { CloseOutlined, MenuOutlined } from '@ant-design/icons';
import { colors } from '@common/colors';
import { BreadcrumbNavigation } from '@components/organisms';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button, Skeleton, Spin, Tooltip } from 'antd';

export const PrivateTemplate = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const {
    profile: { data: profile, isLoading },
  } = useAuth();

  useEffect(() => {
    if (!isCollapsed) {
      const timer = setTimeout(() => {
        setShowFooter(true);
      }, 200);

      return () => clearTimeout(timer);
    } else {
      setShowFooter(false);
    }
  }, [isCollapsed]);

  if (isLoading) {
    return <Skeleton loading className='w-full min-h-screen' />;
  }

  if (!isLoading && !profile) {
    return <Navigate to='/login' />;
  }

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <Spin spinning={isLoading}>
      <div className='flex w-full !h-screen'>
        <nav
          className={`fixed md:relative flex flex-col transition-all duration-300 ease-in-out z-50 h-screen overflow-y-hidden ${menuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
            } ${isCollapsed ? 'w-25' : 'w-72'}`}
          style={{ backgroundColor: colors.darkGray }}
        >
          {!menuOpen && (<Button
            type="text"
            onClick={toggleCollapsed}
            className={`absolute ${isCollapsed ? 'left-16' : 'left-63'} top-2 z-10 flex items-center justify-center w-8 h-8 bg-darkGreen hover:!text-primary !text-white/30`}
            icon={isCollapsed ? <Tooltip placement="right" title="Expandir menu"><CaretRight size={20} /></Tooltip> : <Tooltip placement="right" title="Recolher menu"><CaretLeft size={20} /></Tooltip>}
          />)}
          <div className='flex-1 '>
            <div className={`${isCollapsed ? 'p-2 flex items-center justify-center' : 'p-4'}`}>
              {isCollapsed ? (
                <div className='w-12 h-12 flex items-center justify-center'>
                  <img
                    src={'/ruppert-logo-escritorio.png'}
                    alt="Logo"
                    className='w-full h-full object-contain'
                  />
                </div>
              ) : (
                <div className='w-12 h-12 flex items-center justify-center'>
                  <img
                    src={'/ruppert-logo-escritorio.png'}
                    alt="Logo"
                    className='w-full h-full object-contain'
                  />
                </div>
              )}
            </div>
            <div className='flex-1 p-4'>
              <MainMenu
                onMenuItemClick={handleCloseMenu}
                collapsed={isCollapsed}
              />
            </div>
          </div>
          {!isCollapsed && (
            <div className={`p-6 mt-auto transition-opacity duration-300 ${showFooter ? 'opacity-100' : 'opacity-0'}`}>
              <span className='text-zinc-500 text-xs block uppercase font-extralight mb-2'>
                Powered by
              </span>
              <img
                src='/logo-horizontal.svg'
                alt='Ruppert'
                className='w-auto h-4'
              />
            </div>
          )}
        </nav>

        {menuOpen && (
          <div className="fixed inset-0 bg-[#575E60] bg-opacity-50 md:hidden z-40" />
        )}

        <main className='flex-1 flex flex-col overflow-hidden bg-zinc-50'>
          <div className='h-14 bg-white px-6 py-4 border-b border-zinc-200 flex items-center justify-between'>
            <div className='flex items-center gap-4 '>
              <button
                className={`md:hidden ${menuOpen ? 'fixed top-4 right-2' : ''} z-50 p-2`}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? (
                  <CloseOutlined className="!text-white text-xl" />
                ) : (
                  <MenuOutlined className="text-xl" />
                )}
              </button>
              <div className="hidden md:flex items-center">
                <BreadcrumbNavigation />
              </div>
              {menuOpen && <div className="md:hidden flex items-center"><BreadcrumbNavigation /></div>}
            </div>
            <UserMenu profile={profile} />
          </div>
          <div className='flex-1 overflow-auto'>
            <Outlet />
          </div>
        </main>
      </div>
    </Spin>
  );
};
